import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import Sidebar from '../../../sidebar/sidebar';
import Header from '../../../header/header';
import config from '../../../../config/config';
import { apiCall } from '../../../../_services/apiCall';
import { generatePeriods, filterPeriods, fyDateRange, cfgLabel, MONTHS_SHORT, useIntensityData, useIntensityFilters, IntensityFilterBar, S } from './intensityShared';

const API = config.POSTLOGIN_API_URL_COMPANY;
const getToday = () => new Date().toISOString().slice(0, 10);

const IntensityQuickView = () => {
  const startingMonth = parseInt(
    JSON.parse(localStorage.getItem('currentUser') || '{}')?.starting_month || '4', 10
  );

  const filters = useIntensityFilters();
  const { fromDate, setFromDate, toDate, setToDate, aggregation, effectiveDates, selectedWeekMonths, selectedQuarters, selectedHalves } = filters;
  const effectiveToDate = effectiveDates.toDate;

  const {
    locations, selectedLocIds, setSelectedLocIds,
    allConfigs, selectedConfigIds, setSelectedConfigIds,
    kpiOptions, selectedKpis, setSelectedKpis,
    denomData, filteredDenomData, selectedDenomIds, setSelectedDenomIds,
    loading,
    tableGroups,
    fetchConfigIds,
  } = useIntensityData({ fromDate: effectiveDates.fromDate, toDate: effectiveDates.toDate, aggregation });

  const [dlOpen,    setDlOpen]    = useState(false);
  const [dlLocWise, setDlLocWise] = useState(false);
  const [dlLoading, setDlLoading] = useState(false);
  const dlRef = useRef(null);

  useEffect(() => {
    const close = e => { if (dlRef.current && !dlRef.current.contains(e.target)) setDlOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    const loadFY = async () => {
      try {
        let years = JSON.parse(localStorage.getItem('financialYearsData') || 'null');
        if (!years) {
          const res = await apiCall(`${API}getFinancialYear`, {}, {});
          if (res.isSuccess) {
            years = res.data?.data || [];
            localStorage.setItem('financialYearsData', JSON.stringify(years));
          }
        }
        if (years?.length) {
          const fy = years[years.length - 1];
          const { fromDate: fyStart, toDate: fyEnd } = fyDateRange(fy, startingMonth);
          const today = getToday();
          setFromDate(fyStart);
          setToDate(today < fyEnd ? today : fyEnd);
        }
      } catch (e) { console.error('loadFY', e); }
    };
    loadFY();
  }, []); // eslint-disable-line

  const allPeriods = useMemo(
    () => generatePeriods(effectiveDates.fromDate, effectiveDates.toDate, aggregation),
    [effectiveDates.fromDate, effectiveDates.toDate, aggregation] // eslint-disable-line
  );
  const filteredPeriods = useMemo(
    () => filterPeriods(allPeriods, aggregation, selectedWeekMonths, selectedQuarters, selectedHalves),
    [allPeriods, aggregation, selectedWeekMonths, selectedQuarters, selectedHalves] // eslint-disable-line
  );
  const totalRows = useMemo(
    () => tableGroups.reduce((acc, g) => acc + g.topRows.length + g.kpiRows.length, 0),
    [tableGroups]
  );

  const getVal = (timePeriods, label) => (timePeriods || []).find(t => t.period === label)?.value ?? null;

  const fmtNum = v => v == null
    ? <span style={{ color: '#9ca3af', fontSize: 11 }}>—</span>
    : <span style={{ color: '#2b7a9e', fontWeight: 600 }}>
        {Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </span>;

  const fmtRatio = (num, den) => {
    if (num == null || den == null || den === 0)
      return <span style={{ color: '#9ca3af', fontSize: 11 }}>—</span>;
    return (num / den).toLocaleString(undefined, { maximumFractionDigits: 4 });
  };

  const buildSheetRows = (groups, denomsForSheet) => {
    const rows = [['#', 'Intensity Metric', 'Unit', ...filteredPeriods.map(p => p.label)]];
    let rowNum = 0;
    groups.forEach(({ topRows, kpiRows }) => {
      [...topRows, ...kpiRows].forEach(row => {
        if (denomsForSheet.length === 0) {
          rows.push([++rowNum, row.label, row.unit || '', ...filteredPeriods.map(p => getVal(row.timePeriods, p.label) ?? '')]);
        } else {
          denomsForSheet.forEach(denom => {
            const unit = row.unit && denom.unit ? `${row.unit}/${denom.unit}` : row.unit || denom.unit || '';
            rows.push([++rowNum, `${row.label} per ${denom.title}`, unit,
              ...filteredPeriods.map(p => {
                const n = getVal(row.timePeriods, p.label);
                const d = getVal(denom.timePeriods, p.label);
                return (n != null && d != null && d !== 0) ? +(n / d).toFixed(4) : '';
              }),
            ]);
          });
        }
      });
    });
    return rows;
  };

  const applyColWidths = ws => {
    ws['!cols'] = [{ wch: 5 }, { wch: 48 }, { wch: 12 }, ...filteredPeriods.map(() => ({ wch: 13 }))];
  };

  const exportCombined = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(buildSheetRows(tableGroups, filteredDenomData));
    applyColWidths(ws);
    XLSX.utils.book_append_sheet(wb, ws, 'All Locations');
    XLSX.writeFile(wb, `intensity_${fromDate}_to_${effectiveToDate}.xlsx`);
    setDlOpen(false);
  };

  const exportLocationWise = async () => {
    setDlLoading(true);
    setDlOpen(false);
    try {
      const wb = XLSX.utils.book_new();
      for (const locId of selectedLocIds) {
        const numP = new URLSearchParams();
        fetchConfigIds.forEach(id => numP.append('configIds', id));
        numP.set('locationIds', locId);
        numP.set('fromDate', fromDate);
        numP.set('toDate', effectiveToDate);
        numP.set('aggregation', aggregation);

        const denP = new URLSearchParams({ locationIds: locId, fromDate, toDate: effectiveToDate, aggregation });

        const [numRes, denRes] = await Promise.all([
          apiCall(`${API}intensity/data?${numP}`, {}, {}, 'GET'),
          apiCall(`${API}intensity/denominators?${denP}`, {}, {}, 'GET'),
        ]);

        const locData   = numRes?.data?.data || [];
        const locDenoms = (denRes?.data?.data || []).filter(d => selectedDenomIds.includes(d.title));

        const locGroups = fetchConfigIds
          .map(configId => ({
            configId,
            cfg:     allConfigs.find(c => c.id === configId),
            topRows: selectedConfigIds.includes(configId)
              ? locData.filter(r => r.configId === configId && r.type === 'topLevel')
              : [],
            kpiRows: locData
              .filter(r => r.configId === configId && r.type === 'kpiLevel')
              .filter(r => selectedKpis.includes(r.label)),
          }))
          .filter(g => g.topRows.length > 0 || g.kpiRows.length > 0);

        const loc = locations.find(l => l.id === locId);
        const sheetName = (loc?.unitCode || loc?.location?.city || `Loc ${locId}`).slice(0, 31);
        const ws = XLSX.utils.aoa_to_sheet(buildSheetRows(locGroups, locDenoms));
        applyColWidths(ws);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      }
      XLSX.writeFile(wb, `intensity_location_wise_${fromDate}_to_${effectiveToDate}.xlsx`);
    } catch (e) {
      console.error('exportLocationWise error:', e);
    } finally {
      setDlLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px 24px', fontFamily: "'Inter', -apple-system, sans-serif", fontSize: 14, color: '#1f2937', background: '#f8fafc', minHeight: '100%' }}>

      <IntensityFilterBar
        filters={filters}
        data={{ locations, selectedLocIds, setSelectedLocIds, allConfigs, selectedConfigIds, setSelectedConfigIds, kpiOptions, selectedKpis, setSelectedKpis, denomData, selectedDenomIds, setSelectedDenomIds }}
        totalRows={totalRows}
        periodCount={filteredPeriods.length}
        allPeriods={allPeriods}
      />

      <div style={TS.banner}>
        <div>
          <div style={TS.bannerTitle}>Intensity Dashboard</div>
          <div style={TS.bannerSubtitle}>Intensity ratios across configured types</div>
        </div>
        <div ref={dlRef} style={{ position: 'relative' }}>
          <button
            style={{ ...TS.btnExcel, opacity: (filteredPeriods.length === 0 || dlLoading) ? 0.5 : 1, cursor: (filteredPeriods.length === 0 || dlLoading) ? 'not-allowed' : 'pointer' }}
            onClick={() => !dlLoading && filteredPeriods.length > 0 && setDlOpen(o => !o)}
            disabled={filteredPeriods.length === 0 || dlLoading}
          >
            {dlLoading ? 'Preparing…' : 'Download Excel'}
            {!dlLoading && <span style={{ fontSize: 9, marginLeft: 6 }}>{dlOpen ? '▲' : '▼'}</span>}
          </button>
          {dlOpen && (
            <div style={TS.dlPopover}>
              <label style={TS.dlCheckRow}>
                <input type="checkbox" checked={dlLocWise} onChange={e => setDlLocWise(e.target.checked)} style={{ cursor: 'pointer', accentColor: '#16a34a', width: 15, height: 15 }} />
                <div>
                  <div style={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>Location-wise</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                    {selectedLocIds.length} sheet{selectedLocIds.length !== 1 ? 's' : ''}, one per location
                  </div>
                </div>
              </label>
              <div style={TS.dlDivider} />
              <button style={TS.dlDownloadBtn} onClick={dlLocWise ? exportLocationWise : exportCombined}>Download</button>
            </div>
          )}
        </div>
      </div>

      <div style={TS.tableCard}>
        {loading ? (
          <div style={TS.stateCenter}>
            <div style={S.spinner} />
            <span style={{ color: '#6b7280' }}>Loading intensity data…</span>
          </div>
        ) : selectedLocIds.length === 0 ? (
          <div style={TS.stateCenter}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📍</div>
            <div style={{ fontWeight: 600, color: '#374151' }}>No location selected</div>
            <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>Please select at least one location to view intensity data.</div>
          </div>
        ) : allConfigs.length === 0 ? (
          <div style={TS.stateCenter}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>⚙️</div>
            <div style={{ fontWeight: 600, color: '#374151' }}>No intensity types configured</div>
            <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>Go to Settings → Intensity to set up your types.</div>
          </div>
        ) : filteredPeriods.length === 0 ? (
          <div style={TS.stateCenter}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📊</div>
            <div style={{ fontWeight: 600, color: '#374151' }}>No data for selected filters</div>
            <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>Try adjusting the date range or locations.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={TS.thNum}>#</th>
                  <th style={TS.thLabel}>Intensity Type</th>
                  {filteredPeriods.map(p => {
                    let dateRange = null;
                    if (aggregation === 'weekly' && p.fromDate) {
                      const start = new Date(p.fromDate + 'T00:00:00');
                      const end   = new Date(p.fromDate + 'T00:00:00');
                      end.setDate(end.getDate() + 6);
                      const fmt = d => `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
                      dateRange = `${fmt(start)} – ${fmt(end)}`;
                    } else if (aggregation === 'quarterly' && p.fromDate) {
                      const start = new Date(p.fromDate + 'T00:00:00');
                      const end   = new Date(start.getFullYear(), start.getMonth() + 3, 0);
                      dateRange = `${MONTHS_SHORT[start.getMonth()]} – ${MONTHS_SHORT[end.getMonth()]} ${end.getFullYear()}`;
                    } else if (aggregation === 'half-yearly' && p.fromDate) {
                      const start = new Date(p.fromDate + 'T00:00:00');
                      const end   = new Date(start.getFullYear(), start.getMonth() + 6, 0);
                      const sm = MONTHS_SHORT[start.getMonth()], em = MONTHS_SHORT[end.getMonth()];
                      dateRange = start.getFullYear() === end.getFullYear()
                        ? `${sm} – ${em} ${end.getFullYear()}`
                        : `${sm} ${start.getFullYear()} – ${em} ${end.getFullYear()}`;
                    }
                    return (
                      <th key={p.key} style={TS.thPeriod}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <span style={{ fontSize: 14 }}>📅</span>
                          <span>{p.label}</span>
                          {dateRange && <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 400, letterSpacing: 0 }}>{dateRange}</span>}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let rowNum = 0;
                  return tableGroups.map(({ configId, cfg, topRows, kpiRows }) => (
                    <React.Fragment key={configId}>
                      <tr>
                        <td colSpan={2 + filteredPeriods.length} style={TS.groupHeader}>📋 {cfgLabel(cfg)}</td>
                      </tr>
                      {[...topRows, ...kpiRows].map((row, ri) => {
                        rowNum++;
                        const isTop = row.type === 'topLevel';
                        const currentNum = rowNum;
                        const rowHasData = filteredPeriods.some(p => getVal(row.timePeriods, p.label) != null);
                        return (
                          <React.Fragment key={`${configId}-${ri}`}>
                            <tr style={{ background: '#fff' }}>
                              <td style={TS.tdNum}>{currentNum}</td>
                              <td style={{ ...TS.tdLabel, borderLeft: isTop ? '3px solid #3b82f6' : '3px solid #10b981', paddingLeft: 14 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ flex: 1, fontWeight: 500, color: '#111827' }}>{row.label}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                    {!isTop && <span style={TS.kpiBadge}>KPI</span>}
                                    {rowHasData && row.unit && <span style={TS.unitBadge}>{row.unit}</span>}
                                  </div>
                                </div>
                              </td>
                              {filteredPeriods.map(p => (
                                <td key={p.key} style={{ ...TS.tdVal, borderBottom: filteredDenomData.length > 0 ? 'none' : '1px solid #f1f5f9' }}>
                                  {fmtNum(getVal(row.timePeriods, p.label))}
                                </td>
                              ))}
                            </tr>
                            {filteredDenomData.map((denom, di) => {
                              const isLastDenom = di === filteredDenomData.length - 1;
                              const ratioHasData = filteredPeriods.some(p => {
                                const n = getVal(row.timePeriods, p.label);
                                const d = getVal(denom.timePeriods, p.label);
                                return n != null && d != null && d !== 0;
                              });
                              return (
                                <tr key={`${configId}-${ri}-d${di}`} style={{ background: '#fafafa' }}>
                                  <td style={{ ...TS.tdNum, background: '#fafafa', borderBottom: isLastDenom ? '2px solid #e5e7eb' : '1px solid #f1f5f9' }} />
                                  <td style={{ ...TS.tdLabel, paddingLeft: 28, background: '#fafafa', borderLeft: isTop ? '3px solid #bfdbfe' : '3px solid #a7f3d0', borderBottom: isLastDenom ? '2px solid #e5e7eb' : '1px solid #f1f5f9' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span style={{ flex: 1, fontSize: 12, color: '#374151' }}>{row.label} per {denom.title}</span>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                        {ratioHasData && (row.unit || denom.unit) && (
                                          <span style={TS.unitBadge}>
                                            {row.unit && denom.unit ? `${row.unit} / ${denom.unit}` : row.unit || denom.unit}
                                          </span>
                                        )}
                                        {denom.source && <span style={TS.srcBadge}>{denom.source}</span>}
                                      </div>
                                    </div>
                                  </td>
                                  {filteredPeriods.map(p => (
                                    <td key={p.key} style={{ ...TS.tdVal, fontSize: 12, color: '#6b7280', background: '#fafafa', borderBottom: isLastDenom ? '2px solid #e5e7eb' : '1px solid #f1f5f9' }}>
                                      {fmtRatio(getVal(row.timePeriods, p.label), getVal(denom.timePeriods, p.label))}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const TS = {
  banner: {
    background: '#fff', border: '1px solid #dde3ed',
    borderRadius: 10, padding: '16px 20px', marginBottom: 16,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  bannerTitle:    { fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 2 },
  bannerSubtitle: { fontSize: 13, color: '#64748b' },
  btnExcel: {
    height: 36, padding: '0 18px', border: '1.5px solid #15803d', borderRadius: 8,
    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
    color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6,
    boxShadow: '0 1px 4px rgba(21,128,61,0.25)',
    transition: 'box-shadow 0.15s, opacity 0.15s',
  },
  tableCard: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
  thNum: {
    position: 'sticky', left: 0, zIndex: 11, background: '#f8fafc',
    width: 40, padding: '11px 10px', textAlign: 'center', fontWeight: 700,
    color: '#6b7280', fontSize: 12, textTransform: 'uppercase',
    borderBottom: '2px solid #e5e7eb', boxShadow: 'inset -1px 0 0 #e5e7eb',
  },
  thLabel: {
    position: 'sticky', left: 40, zIndex: 10, background: '#f8fafc',
    padding: '11px 16px', textAlign: 'left', fontWeight: 700,
    color: '#374151', fontSize: 12, textTransform: 'uppercase',
    letterSpacing: '0.05em', borderBottom: '2px solid #e5e7eb',
    minWidth: 240, whiteSpace: 'nowrap',
    boxShadow: '2px 0 4px -1px rgba(0,0,0,0.08)',
  },
  thPeriod: {
    padding: '8px 10px', textAlign: 'center', fontWeight: 600,
    color: '#374151', fontSize: 12, borderBottom: '2px solid #e5e7eb',
    borderLeft: '1px solid #e5e7eb', minWidth: 90, whiteSpace: 'nowrap',
  },
  groupHeader: {
    padding: '9px 16px', background: 'rgba(134,239,172,0.25)',
    fontWeight: 700, fontSize: 13, color: '#065f46',
    borderTop: '1px solid #dcfce7', borderBottom: '1px solid #dcfce7',
    textAlign: 'center', letterSpacing: '0.02em',
  },
  tdNum: {
    position: 'sticky', left: 0, zIndex: 6, background: '#fff',
    width: 40, padding: '10px 10px', textAlign: 'center', fontSize: 12,
    color: '#9ca3af', borderBottom: '1px solid #f1f5f9', fontWeight: 500,
    boxShadow: 'inset -1px 0 0 #e5e7eb',
  },
  tdLabel: {
    position: 'sticky', left: 40, zIndex: 5, background: '#fff',
    padding: '10px 16px', borderBottom: '1px solid #f1f5f9',
    boxShadow: '2px 0 4px -1px rgba(0,0,0,0.08)',
  },
  tdVal: {
    padding: '10px 14px', textAlign: 'center',
    borderBottom: '1px solid #f1f5f9', borderLeft: '1px solid #f1f5f9',
    fontVariantNumeric: 'tabular-nums',
  },
  kpiBadge:  { display: 'inline-block', fontSize: 10, color: '#0369a1', background: '#e0f2fe', borderRadius: 3, padding: '1px 5px', fontWeight: 600 },
  unitBadge: { display: 'inline-block', fontSize: 10, color: '#6b21a8', background: '#f3e8ff', borderRadius: 3, padding: '1px 5px', fontWeight: 500 },
  srcBadge:  { fontSize: 10, color: '#9ca3af', background: '#f3f4f6', borderRadius: 3, padding: '1px 5px', textTransform: 'capitalize' },
  stateCenter: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 4 },
  dlPopover: {
    position: 'absolute', right: 0, top: 'calc(100% + 6px)',
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 999, minWidth: 220, padding: 14,
  },
  dlCheckRow:    { display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', userSelect: 'none' },
  dlDivider:     { height: 1, background: '#f1f5f9', margin: '12px 0' },
  dlDownloadBtn: { width: '100%', height: 34, border: 'none', borderRadius: 7, background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
};

const Intensity = ({ location }) => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  return (
    <div className="d-flex flex-row mainclass" style={{ height: '100vh', overflow: 'hidden' }}>
      <div style={{ flex: sidebarExpanded ? '0 0 21%' : '0 0 60px', position: 'sticky', top: 0, zIndex: 999, height: '100vh', transition: 'flex 0.3s ease' }}>
        <Sidebar dataFromParent={location.pathname} onSidebarToggle={setSidebarExpanded} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 999, borderBottom: '1px solid #eee' }}>
          <Header />
        </div>
        <div style={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', background: '#f8fafc' }}>
          <IntensityQuickView />
        </div>
      </div>
    </div>
  );
};

export default Intensity;
